/* ═══════════════════════════════════════════════════════════════════════════
   THE PAYMENT AUTHORISATION

   An amount is held when the appointment is made, released if the customer
   cancels in time, and charged when the appointment is carried out. Three
   events, four states, and the whole of it is here rather than in a component
   because every one of these transitions is somebody's money.

       reserved ──── cancel before the deadline ──→ released
                ──── cancel after the deadline  ──→ forfeited
                ──── the appointment happened   ──→ captured
                ──── nobody did anything        ──→ expired

   ── Why a hold and not a payment ──────────────────────────────────────────

   The business's problem is the no-show: a chair empty at half past two that
   somebody else would have taken. Charging up front solves it and creates a
   worse problem — the customer has paid for something they have not had, and
   every cancellation becomes a refund. A hold is the honest instrument: the
   money never leaves the customer's account, the business knows it is there,
   and it is either let go or taken, once, at the end.

   ── The deadline, and why 24 hours ────────────────────────────────────────

   `FREE_HOURS` is the platform's default and the tenant may raise it. Twenty
   four hours is the number a business can actually refill a slot in, and it is
   the number the trade uses, so it is what a customer expects.

   The rule is stated before the button, never after it: the exact moment free
   cancellation ends, in the customer's own words, on the screen where they
   authorise. A cancellation charge that a customer did not see coming is not
   a term, it is a surprise, and the Consumer Authority treats it as one.

   ── What Norwegian law does and does not give here ────────────────────────

   Angrerettloven's fourteen-day right of withdrawal does NOT cover a service
   booked for a specific date or period — §22(m) exempts them, which is why a
   hairdresser may charge for a missed appointment at all and a shampoo
   subscription may not be treated the same way. So the cancellation term is a
   matter of what was agreed, which puts the whole weight on it having been
   agreed: shown plainly, before the money is committed, in language somebody
   reads rather than scrolls past.

   ── What this build does not do ───────────────────────────────────────────

   Nothing is held and nothing is charged. There are no Vipps merchant keys,
   so `authorise` records the intention, the state machine runs for real, and
   every screen says so in as many words. When the keys arrive, one adapter
   changes and none of this does.
   ═══════════════════════════════════════════════════════════════════════ */

/** Hours before the start that free cancellation ends. The tenant may raise
    it — never silently lower it, because a customer who has already
    authorised agreed to the number they were shown. */
export const FREE_HOURS = 24;

/**
 * A grace period after booking, during which cancelling is free whatever the
 * deadline says.
 *
 * Without it the 24-hour rule has a hole that falls on exactly the wrong
 * person: book at nine in the morning for two in the afternoon, change your
 * mind at five past nine, and you are charged in full — because the
 * appointment was always inside the window, so there never was a free period
 * to be inside of. A rule that gives a customer no way out at all is not a
 * cancellation policy, and a regulator reading it would say the same.
 *
 * Thirty minutes is enough to undo a mistake and far too short to be a way of
 * holding a slot for nothing.
 */
export const GRACE_MIN = 30;

/** How long a hold lasts if nothing happens to it. Card and wallet schemes
    let a reservation lapse somewhere around a week; past that the business
    has to ask again rather than sit on somebody's money indefinitely. */
export const HOLD_DAYS = 7;

export const STATES = ['reserved', 'released', 'captured', 'forfeited', 'expired'];

/** A state nothing more will happen to. */
export const isSettled = state => state !== 'reserved';

/** "2026-09-19 14:30" → a Date. The stored form is local, not UTC, because an
    appointment at half past two is half past two where the business is. */
export function startsAt(record) {
  const t = String(record?.time || '');
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
}

/** The moment free cancellation ends. */
export function freeUntil(record, hours = FREE_HOURS) {
  const start = startsAt(record);
  if (!start) return null;
  return new Date(start.getTime() - hours * 3600 * 1000);
}

/**
 * Is there still time to cancel without being charged?
 *
 * Free if EITHER is true — the deadline has not passed, or the booking was
 * made in the last half hour. The second is the grace period above, and it is
 * what stops a same-day booking from being uncancellable from the moment it
 * is made.
 */
export function canCancelFree(record, now = new Date(), hours = FREE_HOURS) {
  if (withinGrace(record, now)) return true;
  const until = freeUntil(record, hours);
  return until ? now < until : true;
}

/** Was this booked in the last few minutes? */
export function withinGrace(record, now = new Date(), minutes = GRACE_MIN) {
  const made = record?.auth?.created || record?.created;
  if (!made) return false;
  const t = new Date(made).getTime();
  if (Number.isNaN(t)) return false;
  return now - t < minutes * 60000;
}

/** How long is left, in whole hours. Negative once the deadline has gone. */
export function hoursLeft(record, now = new Date(), hours = FREE_HOURS) {
  const until = freeUntil(record, hours);
  if (!until) return null;
  return Math.floor((until - now) / 3600000);
}

/**
 * The deadline in words a person reads rather than parses.
 * "until Thursday 14:30" — the day and the time, because a customer deciding
 * whether to cancel tonight needs to know if tonight is still in time.
 */
export function deadlineWords(record, now = new Date(), hours = FREE_HOURS) {
  const until = freeUntil(record, hours);
  if (!until) return null;
  const clock = String(until.getHours()).padStart(2, '0') + ':' +
                String(until.getMinutes()).padStart(2, '0');

  const midnight = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((midnight(until) - midnight(now)) / 86400000);

  if (days < 0) return 'the deadline has passed';
  if (days === 0) return `today at ${clock}`;
  if (days === 1) return `tomorrow at ${clock}`;
  if (days < 7) {
    return until.toLocaleDateString('en-GB', { weekday: 'long' }) + ` at ${clock}`;
  }
  return until.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) + ` at ${clock}`;
}

/** Minutes left of the grace period, or null when it does not apply. */
export function graceLeft(record, now = new Date(), minutes = GRACE_MIN) {
  if (!withinGrace(record, now, minutes)) return null;
  const made = new Date(record?.auth?.created || record?.created);
  const ends = new Date(made.getTime() + minutes * 60000);
  return Math.max(1, Math.ceil((ends - now) / 60000));
}

/**
 * The whole tail of the sentence "Cancel free ___", with its preposition, so
 * that no screen has to guess which one to put in front of it.
 *
 * Two cases and two prepositions: a normal booking is free UNTIL a moment,
 * and a same-day one is free FOR a few more minutes. Returning the bare
 * moment and leaving "until" to the caller produced "Free until 30 more
 * minutes", which is how you can tell a sentence was assembled rather than
 * written.
 */
export function freePhrase(record, now = new Date(), hours = FREE_HOURS) {
  const left = graceLeft(record, now);
  const until = freeUntil(record, hours);
  /* Inside the 24 hours already: the grace period is all there is. */
  if (left != null && until && now >= until) {
    return `for ${left} more ${left === 1 ? 'minute' : 'minutes'}`;
  }
  const when = deadlineWords(record, now, hours);
  if (!when) return null;
  if (when === 'the deadline has passed') return null;
  return `until ${when}`;
}

/**
 * What one event does to an authorisation.
 *
 * Pure, and deliberately strict: an unknown event or an impossible transition
 * throws rather than returning the state unchanged. A capture that silently
 * did nothing is the kind of bug that is only discovered in the accounts.
 *
 * @param {{state: string, amount: number}} auth
 * @param {'cancel'|'complete'|'expire'} event
 * @param {{record?: object, now?: Date, hours?: number}} ctx
 */
export function apply(auth, event, ctx = {}) {
  const now = ctx.now || new Date();
  if (!auth) throw new Error('There is no authorisation on this appointment.');
  if (isSettled(auth.state)) {
    throw new Error(`This authorisation is already ${auth.state}.`);
  }

  if (event === 'cancel') {
    /* The one decision that costs somebody money, and it is made by the clock
       rather than by anybody's judgement. */
    const free = canCancelFree(ctx.record, now, ctx.hours);
    return {
      ...auth,
      state: free ? 'released' : 'forfeited',
      settled: now.toISOString(),
      /* A forfeited hold takes the agreed fee, not the whole amount, unless
         the business set them equal. Stated on the screen before the
         customer ever authorised. */
      charged: free ? 0 : (auth.fee ?? auth.amount),
    };
  }

  if (event === 'complete') {
    return {
      ...auth,
      state: 'captured',
      settled: now.toISOString(),
      charged: auth.amount,
    };
  }

  if (event === 'expire') {
    return { ...auth, state: 'expired', settled: now.toISOString(), charged: 0 };
  }

  throw new Error(`Unknown event "${event}".`);
}

/** Has the hold lapsed on its own? */
export function hasExpired(auth, now = new Date(), days = HOLD_DAYS) {
  if (!auth || auth.state !== 'reserved' || !auth.created) return false;
  return now - new Date(auth.created) > days * 86400000;
}

/** What the customer is told each state means. One sentence, no jargon. */
export const MEANING = {
  reserved:  'Held on your card. Not charged.',
  released:  'The hold was let go. Nothing was charged.',
  captured:  'Charged after the appointment.',
  forfeited: 'Cancelled after the deadline, so the agreed fee was charged.',
  expired:   'The hold lapsed. Nothing was charged.',
};

/** And what the business is told. The same facts, the other way round. */
export const MEANING_BUSINESS = {
  reserved:  'Authorised. Capture it when the appointment is carried out.',
  released:  'Cancelled in time. Released.',
  captured:  'Charged.',
  forfeited: 'Cancelled late. The agreed fee was charged.',
  expired:   'The hold lapsed before it was captured.',
};

/**
 * The terms, in the words the customer sees before authorising. Built here
 * rather than written into a component so that the screen that takes the
 * authorisation and the screen that later cancels cannot describe the same
 * agreement differently.
 */
export function terms({ amount, fee, record, hours = FREE_HOURS, now = new Date(), method }) {
  const kr = n => Number(n || 0).toLocaleString('nb-NO') + ' kr';
  const until = freeUntil(record, hours);
  /* Is this appointment already inside the notice window? A same-day booking
     usually is, and saying "cancel free up to 24 hours before" to somebody
     booking for this afternoon is worse than saying nothing: it describes a
     free period they do not have. */
  const inside = !until || now >= until;
  const phrase = freePhrase(record, now, hours);
  const late = fee && fee !== amount
    ? `${kr(fee)} is charged`
    : `the ${kr(amount)} is charged`;

  /* Where the money is held. This used to say "on your card" whatever had
     happened, and that stopped being true the moment there were two roads: a
     Vipps authorisation is held against whatever the customer has set up in
     Vipps, and telling them it is on their card is describing a mechanism
     they did not use. Terms that misdescribe the mechanism are the terms
     somebody disputes. */
  const where = method === 'card' ? ' on your card'
    : method === 'vipps' ? ' in Vipps'
    : '';

  return [
    `${kr(amount)} is held${where} now. Nothing is charged today.`,

    inside
      ? `This one is less than ${hours} hours away, so the free period is the ` +
        `${GRACE_MIN} minutes after you book. After that, ${late}.`
      : `Cancel free ${phrase} — the hold is let go the same day. ` +
        `Booked closer than that another time, and you still get ` +
        `${GRACE_MIN} minutes to undo it.`,

    inside
      ? `Not turning up is the same as cancelling late: ${late}.`
      : `Cancel later than that, or not turn up, and ${late}.`,

    'The full amount is charged after the appointment has been carried out.',
  ];
}
